/* eslint-disable no-undef */
import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle
} from 'react';
import PropTypes from 'prop-types';

const TurnstileWidget = forwardRef(({ onSuccess, theme = 'light' }, ref) => {
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);

  const sitekey = process.env.REACT_APP_API_KEY_TURNSTILE;

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
    }
  }));

  useEffect(() => {
    if (!sitekey || typeof sitekey !== 'string') {
      console.error('Turnstile sitekey missing or invalid:', sitekey);
      return;
    }

    const loadScript = () => {
      return new Promise((resolve, reject) => {
        if (window.turnstile) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
        script.async = true;
        script.defer = true;

        script.onload = () => resolve();
        script.onerror = () =>
          reject(new Error('Failed to load Turnstile script'));

        document.head.appendChild(script);
      });
    };

    loadScript()
      .then(() => {
        if (turnstileRef.current && !widgetIdRef.current) {
          widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
            sitekey,
            theme,
            callback: token => onSuccess(token),
            'expired-callback': () => onSuccess(null),
            'error-callback': () => onSuccess(null)
          });
        }
      })
      .catch(err => {
        console.error('Turnstile load/render error:', err);
      });

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [onSuccess, theme, sitekey]);

  return (
    <div className="flex items-center gap-2">
      <div ref={turnstileRef} className="cf-turnstile" />
    </div>
  );
});

TurnstileWidget.displayName = 'TurnstileWidget';

TurnstileWidget.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  theme: PropTypes.oneOf(['light', 'dark'])
};

export default TurnstileWidget;
